import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import kycService, { checkDocumentExists } from '../../services/onboarding/kycService';
import preRegistrationService from '../../services/onboarding/preRegistrationService';
import { useOnboarding } from './OnboardingContext';
import {
  BusinessTypeEnum,
  NatureOfBusinessEnum,
  getBusinessTypeOptions,
  getNatureOfBusinessOptions,
  getBuyerSellerTypeOptions,
  getBusinessTypeEnumName,
  getNatureOfBusinessEnumName,
  getBuyerSellerEnumName,
  getTypeOfIndividualOptions,
  getTypeOfIndividualEnumName
} from './enumData';
import { getNatureOfBusinessEnumOptions, getNatureOfBusinessValueByEnumName } from './enumData';
import PillSelector from '../PillSelector';
import { useIsMobile } from 'lib/utils';
import { set, update } from 'lodash';

const validationSchema = Yup.object().shape({
  buyerSellerType: Yup.number().when('typeOfIndividual', {
    is: (typeOfIndividual) => {
      const enumName = getTypeOfIndividualEnumName(Number(typeOfIndividual));
      // Only required for BusinessOrganisation
      return enumName !== 'DriverAndVehicleOwner' && enumName !== 'TransportCompany';
    },
    then: Yup.number().required('Please select an option'),
    otherwise: Yup.number().notRequired(),
  }),
  businessType: Yup.number().when('typeOfIndividual', {
    is: (typeOfIndividual) => {
      const enumName = getTypeOfIndividualEnumName(Number(typeOfIndividual));
      // Required for BusinessOrganisation and TransportCompany, not for DriverAndVehicleOwner
      return enumName !== 'DriverAndVehicleOwner';
    },
    then: Yup.number().required('Business Type is required'),
    otherwise: Yup.number().notRequired(),
  }),
  natureOfBusiness: Yup.number().when('typeOfIndividual', {
    is: (typeOfIndividual) => {
      const enumName = getTypeOfIndividualEnumName(Number(typeOfIndividual));
      // Only required for BusinessOrganisation
      return enumName !== 'DriverAndVehicleOwner' && enumName !== 'TransportCompany';
    },
    then: Yup.number().required('Nature of Business is required'),
    otherwise: Yup.number().notRequired(),
  }),
  typeOfIndividual: Yup.number().required('Please select an option'),
  hasGST: Yup.boolean().required('Please select an option'),
  gstNumber: Yup.string().when('hasGST', {
    is: true,
    then: Yup.string().required('GST number is required').test(
      'gst-not-exists',
      '', // Empty message since API response provides the actual error
      function () {
        // Return true if no document exists error (validation passes)
        // Return false if document exists error (validation fails)
        return !this.parent.documentExistsErrors?.gstNumber;
      }
    ),
    otherwise: Yup.string(),
  }),
  hasPAN: Yup.boolean().when('hasGST', {
    is: false,
    then: Yup.boolean().required('Please select an option'),
    otherwise: Yup.boolean(),
  }),
  panNumber: Yup.string().when(['hasGST', 'hasPAN'], {
    is: (hasGST, hasPAN) => hasGST === false && hasPAN === true,
    then: Yup.string().required('PAN number is required').test(
      'pan-not-exists',
      '', // Empty message since API response provides the actual error
      function () {
        return !this.parent.documentExistsErrors?.panNumber;
      }
    ),
    otherwise: Yup.string(),
  }),
  hasIndividualDL: Yup.boolean().when(['hasGST', 'hasPAN'], {
    is: (hasGST, hasPAN) => hasGST === false && hasPAN === false,
    then: Yup.boolean().required('Please select an option'),
    otherwise: Yup.boolean(),
  }),
  individualId: Yup.string().when(['hasGST', 'hasPAN', 'hasIndividualDL'], {
    is: (hasGST, hasPAN, hasIndividualDL) => hasGST === false && hasPAN === false && hasIndividualDL === true,
    then: Yup.string().required('ID is required').test(
      'individual-not-exists',
      '', // Empty message since API response provides the actual error
      function () {
        return !this.parent.documentExistsErrors?.individualId;
      }
    ),
    otherwise: Yup.string(),
  }),
  date_of_birth: Yup.string().when(['hasGST', 'hasPAN', 'hasIndividualDL'], {
    is: (hasGST, hasPAN, hasIndividualDL) => hasGST === false && hasPAN === false && hasIndividualDL === true,
    then: Yup.string().required('Date of birth is required'),
    otherwise: Yup.string(),
  }),
  documentExistsErrors: Yup.object(),
});

const BusinessDetails = forwardRef(({ title, onComplete, isNextButtonDisable, setIsNextButtonDisable }, ref) => {
  const isMobile = useIsMobile();
  const [isAddressFromDocMissing, setIsAddressFromDocMissing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [missingAddressFields, setMissingAddressFields] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [businessInfo, setBusinessInfo] = useState({
    gst: null,
    pan: null,
    individual: null
  });
  console.log("BusinessInfo", businessInfo)
  const [fullBusinessInfo, setFullBusinessInfo] = useState({
    gst: null,
    pan: null,
    individual: null
  });
  const [validatingField, setValidatingField] = useState(null);
  const [validatedPincode, setValidatedPincode] = useState('');
  const [authorizedSignatories, setAuthorizedSignatories] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [selectedAuthorizedSignatory, setSelectedAuthorizedSignatory] = useState(null);
  const [documentExistsErrors, setDocumentExistsErrors] = useState({});
  const [prefetchedManualAddress, setPrefetchedManualAddress] = useState({});
  const [checkingDocumentExists, setCheckingDocumentExists] = useState(null);
  const {
    updateSection,
    markStepComplete,
    markStepIncomplete,
    getStepData,
    isStepComplete
  } = useOnboarding();

  // Get existing data for this step
  const existingData = getStepData(1); // Step 1 is businessDetails
  console.log("existingData", existingData)

  // Initialize state from existing data - RUN ONLY ONCE ON MOUNT
  useEffect(() => {
    if (existingData.validatedInfo) {
      setBusinessInfo(existingData.validatedInfo);
    }
    if (existingData.fullValidatedInfo) {
      setFullBusinessInfo(existingData.fullValidatedInfo);
    }
    if (existingData.validatedPincode) {
      setValidatedPincode(existingData.validatedPincode);
    }
    if (existingData.authorizedSignatories) {
      setAuthorizedSignatories(existingData.authorizedSignatories);
    }
    if (existingData.companyName) {
      setCompanyName(existingData.companyName);
    }
    if (existingData.selectedAuthorizedSignatory) {
      setSelectedAuthorizedSignatory(existingData.selectedAuthorizedSignatory);
    }
    if (existingData.prefetchedManualAddress) {
      setPrefetchedManualAddress(existingData.prefetchedManualAddress);
    }
    if (existingData.isAddressFromDocMissing !== undefined) {
      setIsAddressFromDocMissing(existingData.isAddressFromDocMissing);
    }
    if (existingData.missingAddressFields) {
      setMissingAddressFields(existingData.missingAddressFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to prevent overwriting local validation state when context updates

  console.log(businessInfo, "businessInfo")

  // Function to convert only letters to uppercase while preserving numbers and special characters
  const convertLettersToUppercase = (value) => {
    return value.replace(/[a-zA-Z]/g, (letter) => letter.toUpperCase());
  };

  // Function to check if GST already exists
  const checkGSTExists = async (gstNumber) => {
    try {
      setCheckingDocumentExists('gstNumber');
      const response = await checkDocumentExists(3, gstNumber);
      if (response?.user_already_exists) {
        setDocumentExistsErrors(prev => ({
          ...prev,
          gstNumber: response?.message || "This GST number is already registered in our system"
        }));
        return true;
      } else {
        setDocumentExistsErrors(prev => ({ ...prev, gstNumber: null }));
        return false;
      }
    } catch (error) {
      console.error('Error checking GST existence:', error);
      setDocumentExistsErrors(prev => ({ ...prev, gstNumber: null }));
      return false;
    } finally {
      setCheckingDocumentExists(null);
    }
  };

  // Function to check if PAN already exists
  const checkPANExists = async (panNumber) => {
    try {
      setCheckingDocumentExists('panNumber');
      const response = await checkDocumentExists(2, panNumber);
      console.log("response---->123", response)
      if (response?.user_already_exists) {
        setDocumentExistsErrors(prev => ({
          ...prev,
          panNumber: response?.message || "This PAN number is already registered in our system"
        }));
        return true;
      } else {
        setDocumentExistsErrors(prev => ({ ...prev, panNumber: null }));
        return false;
      }
    } catch (error) {
      console.error('Error checking PAN existence:', error);
      setDocumentExistsErrors(prev => ({ ...prev, panNumber: null }));
      return false;
    } finally {
      setCheckingDocumentExists(null);
    }
  };

  // Function to check if Individual ID already exists
  const checkIndividualIDExists = async (individualId, dateOfBirth) => {
    try {
      setCheckingDocumentExists('individualId');
      const response = await checkDocumentExists(6, individualId, null, dateOfBirth);
      if (response?.user_already_exists) {
        setDocumentExistsErrors(prev => ({
          ...prev,
          individualId: response?.message || "This Driving License is already registered in our system"
        }));
        return true;
      } else {
        setDocumentExistsErrors(prev => ({ ...prev, individualId: null }));
        return false;
      }
    } catch (error) {
      console.error('Error checking Individual ID existence:', error);
      setDocumentExistsErrors(prev => ({ ...prev, individualId: null }));
      return false;
    } finally {
      setCheckingDocumentExists(null);
    }
  };

  const handleGSTValidation = async (gstNumber, setFieldValue) => {
    try {
      setIsLoading(true);
      setValidatingField('gstNumber');

      // First check if GST already exists
      const gstExists = await checkGSTExists(gstNumber);
      if (gstExists) {
        setIsLoading(false);
        setValidatingField(null);
        return;
      }

      const response = await kycService.verifyDocument(3, gstNumber);
      console.log('response---->123', response);
      if (response.response_code === "100" && response?.result) {
        setBusinessInfo(prev => ({
          ...prev,
          gst: response?.result
        }));
        setFullBusinessInfo(prev => ({
          ...prev,
          gst: response
        }));
        setValidationErrors(prev => ({ ...prev, gstNumber: null }));
        // Extract pincode from registered_address
        const address = response?.result?.primary_business_address?.registered_address;
        if (address) {
          const match = address.match(/(\d{6})(?!.*\d)/);
          if (match) {
            const pincode = match[1];
            setValidatedPincode(pincode);
            console.log('GST Pincode:', pincode);
            updateSection('businessDetails', { validatedPincode: pincode });
          }

          // GST Address Parsing Logic
          const address_List = address.split(', ');

          // Helper to check if value is considered "missing"
          const isMissing = (val) => !val || val.trim() === "" || val.trim() === "." || val.trim().toLowerCase() === "na";
          const getVal = (val) => isMissing(val) ? '' : val.trim();

          // Initialize ALL 7 fields for manualAddress
          const manualAddr = {
            line_1: getVal(address_List[0]),
            line_2: getVal(address_List[1]),
            street_name: getVal(address_List[3]),
            city: address_List.length >= 3 ? getVal(address_List[address_List.length - 3]) : '',
            state: address_List.length >= 2 ? getVal(address_List[address_List.length - 2]) : '',
            zip: address_List.length >= 1 ? getVal(address_List[address_List.length - 1]) : '',
            district_code: '', // Typically missing from GST
          };

          setFieldValue('manualAddress', manualAddr);
          setPrefetchedManualAddress(manualAddr);
          setMissingAddressFields(['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code']);
          setIsAddressFromDocMissing(true);

          // Persist fetched address and source
          updateSection('businessDetails', {
            fetchedRegisteredAddress: address,
            fetchedAddressSource: 'GSTIN',
            prefetchedManualAddress: manualAddr,
            isAddressFromDocMissing: true,
            missingAddressFields: ['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code']
          });
        } else {
          // If no address at all, definitely missing
          setIsAddressFromDocMissing(true);
          setMissingAddressFields(['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code']);
          updateSection('businessDetails', {
            fetchedRegisteredAddress: null,
            fetchedAddressSource: 'GSTIN',
            isAddressFromDocMissing: true,
            missingAddressFields: ['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code']
          });
        }
        // Extract authorized signatories
        setAuthorizedSignatories(response?.result?.authorized_signatory || []);
        // Extract company name
        setCompanyName(response?.result?.legal_name || '');
      } else if (response.response_code === "106") {
        setValidationErrors(prev => ({
          ...prev,
          gstNumber: response.response_message || "Invalid GST Number"
        }));
        setBusinessInfo(prev => ({ ...prev, gst: null }));
        setFullBusinessInfo(prev => ({ ...prev, gst: null }));
        setAuthorizedSignatories([]);
        setCompanyName('');
      } else if (response.response_code === "101") {
        setValidationErrors(prev => ({
          ...prev,
          gstNumber: response.response_message || "No Record Found"
        }));
      } else if (response.success === false || response.response_code !== "100") {
        // Handle any other failure cases (e.g., response_code "108" - Source Error)
        const errorMessage = response.response_message
          ? `Error ${response.response_code}: ${response.response_message}`
          : "GST validation failed. Please try again.";
        setValidationErrors(prev => ({
          ...prev,
          gstNumber: errorMessage
        }));
        setBusinessInfo(prev => ({ ...prev, gst: null }));
        setFullBusinessInfo(prev => ({ ...prev, gst: null }));
        setAuthorizedSignatories([]);
        setCompanyName('');
      }
    } catch (error) {
      setValidationErrors(prev => ({
        ...prev,
        gstNumber: error.message || "Error validating GST"
      }));
      setBusinessInfo(prev => ({ ...prev, gst: null }));
      setFullBusinessInfo(prev => ({ ...prev, gst: null }));
      setAuthorizedSignatories([]);
      setCompanyName('');
    } finally {
      setIsLoading(false);
      setValidatingField(null);
    }
  };

  // const handleBusinessPANValidation = async (panNumber) => {
  //   try {
  //     setIsLoading(true);
  //     setValidatingField('panNumber');

  //     // First check if PAN already exists
  //     const panExists = await checkPANExists(panNumber);
  //     if (panExists) {
  //       setIsLoading(false);
  //       setValidatingField(null);
  //       return;
  //     }

  //     const response = await kycService.verifyDocument(2, panNumber);

  //     console.log('PAN validation response:', response);

  //     if (response.response_code === "100" && response?.result) {
  //       if(response?.result?.pan_type === "Company") {
  //         setBusinessInfo(prev => ({
  //           ...prev,
  //           pan: response?.result
  //         }));
  //         setFullBusinessInfo(prev => ({
  //           ...prev,
  //           pan: response
  //         }));
  //         setValidationErrors(prev => ({ ...prev, panNumber: null }));
  //       }
  //       else {
  //         setBusinessInfo(prev => ({
  //           ...prev,
  //           pan: response?.result
  //         }));
  //         setFullBusinessInfo(prev => ({
  //           ...prev,
  //           pan: response
  //         }));
  //         setValidationErrors(prev => ({ ...prev, panNumber: null }));
  //       }
  //       // Get zip directly from result.user_address.zip
  //       const pincode = response?.result?.user_address?.zip;
  //       if (pincode) {
  //         setValidatedPincode(pincode);
  //         console.log('PAN Pincode:', pincode);
  //         updateSection('businessDetails', { validatedPincode: pincode });
  //       }
  //       // Extract registered address from PAN result and persist
  //       const registeredAddressPAN = response?.result?.user_address?.full || '';
  //       if (registeredAddressPAN) {
  //         updateSection('businessDetails', {
  //           fetchedRegisteredAddress: registeredAddressPAN,
  //           fetchedAddressSource: 'PAN'
  //         });
  //       }else{
  //         setIsAddressFromDocMissing(true);
  //         updateSection('businessDetails', {
  //           fetchedRegisteredAddress: null,
  //           fetchedAddressSource: 'PAN',
  //         });
  //       }
  //     } else if (response.response_code === "106") {
  //       setValidationErrors(prev => ({
  //         ...prev,
  //         panNumber: response.response_message || "Invalid PAN Number"
  //       }));
  //       setBusinessInfo(prev => ({ ...prev, pan: null }));
  //       setFullBusinessInfo(prev => ({ ...prev, pan: null }));
  //     }
  //     else if (response.response_code === "101") {
  //       setValidationErrors(prev => ({
  //         ...prev,
  //         panNumber: response.response_message || "No Record Found"
  //       }));
  //     }
  //   } catch (error) {
  //     setValidationErrors(prev => ({
  //       ...prev,
  //       panNumber: error.message || "Error validating PAN"
  //     }));
  //     setBusinessInfo(prev => ({ ...prev, pan: null }));
  //     setFullBusinessInfo(prev => ({ ...prev, pan: null }));
  //   } finally {
  //     setIsLoading(false);
  //     setValidatingField(null);
  //   }
  // };

  // Recover the "missing" state from saved manualAddress - MOUNT ONLY
  useEffect(() => {
    if (existingData.manualAddress) {
      // If we have manual address data, it means we are in address editing mode
      const hasManualData = Object.values(existingData.manualAddress).some(val => val !== '');
      if (hasManualData) {
        setIsAddressFromDocMissing(true);
        const addressKeys = ['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code'];
        setMissingAddressFields(addressKeys);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBusinessPANValidation = async (panNumber, setFieldValue) => {
    try {
      setIsLoading(true);
      setValidatingField('panNumber');

      const panExists = await checkPANExists(panNumber);
      if (panExists) {
        setIsLoading(false);
        setValidatingField(null);
        return;
      }

      const response = await kycService.verifyDocument(2, panNumber);

      if (response.response_code === "100" && response?.result) {
        setAuthorizedSignatories([]);
        const result = response.result;
        const addressObj = result.user_address || {};

        // Identify all keys for manualAddress
        const addressKeys = ['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code'];
        const isMissing = (val) => !val || typeof val !== 'string' || val.trim() === "" || val.trim() === "." || val.trim().toLowerCase() === "na";
        const getVal = (val) => isMissing(val) ? '' : val.trim();

        const manualAddr = {
          line_1: getVal(addressObj.line_1),
          line_2: getVal(addressObj.line_2),
          street_name: getVal(addressObj.street_name),
          city: getVal(addressObj.city),
          state: getVal(addressObj.state),
          zip: getVal(addressObj.zip),
          district_code: getVal(addressObj.district_code),
        };

        setFieldValue('manualAddress', manualAddr);
        setPrefetchedManualAddress(manualAddr);
        setMissingAddressFields(addressKeys);
        setIsAddressFromDocMissing(true);

        // Update objects for persistence
        updateSection('businessDetails', {
          fetchedRegisteredAddress: addressObj.full,
          fetchedAddressSource: 'PAN',
          prefetchedManualAddress: manualAddr,
          isAddressFromDocMissing: true,
          missingAddressFields: addressKeys
        });

        // 2. Set business info as is
        setBusinessInfo(prev => ({ ...prev, pan: result }));
        setFullBusinessInfo(prev => ({ ...prev, pan: response }));
        setValidationErrors(prev => ({ ...prev, panNumber: null }));

        // 3. Handle Pincode
        if (addressObj.zip) {
          setValidatedPincode(addressObj.zip);
          updateSection('businessDetails', { validatedPincode: addressObj.zip });
        }

        // 4. Persistence for Registered Address
        if (addressObj.full && addressObj.full.trim() !== "") {
          updateSection('businessDetails', {
            fetchedRegisteredAddress: addressObj.full,
            fetchedAddressSource: 'PAN'
          });
        } else {
          updateSection('businessDetails', {
            fetchedRegisteredAddress: null,
            fetchedAddressSource: 'PAN',
          });
        }

      } else if (response.response_code === "106") {
        setValidationErrors(prev => ({
          ...prev,
          panNumber: response.response_message || "Invalid PAN Number"
        }));
        setBusinessInfo(prev => ({ ...prev, pan: null }));
        setFullBusinessInfo(prev => ({ ...prev, pan: null }));
      } else if (response.response_code === "101") {
        setValidationErrors(prev => ({
          ...prev,
          panNumber: response.response_message || "No Record Found"
        }));
      }
    } catch (error) {
      setValidationErrors(prev => ({
        ...prev,
        panNumber: error.message || "Error validating PAN"
      }));
      setBusinessInfo(prev => ({ ...prev, pan: null }));
      setFullBusinessInfo(prev => ({ ...prev, pan: null }));
    } finally {
      setIsLoading(false);
      setValidatingField(null);
    }
  };

  const handleIndividualPANValidation = async (panNumber, dateOfBirth, setFieldValue) => {
    try {
      setIsLoading(true);
      setValidatingField('individualId');

      // First check if Individual ID already exists
      const individualExists = await checkIndividualIDExists(panNumber, dateOfBirth);
      if (individualExists) {
        setIsLoading(false);
        setValidatingField(null);
        return;
      }

      const response = await kycService.verifyDocument(6, panNumber, null, dateOfBirth);

      if (response.response_code === "100" && response?.result) {
        const result = response.result;

        setBusinessInfo(prev => ({
          ...prev,
          individual: result
        }));
        setFullBusinessInfo(prev => ({
          ...prev,
          individual: response
        }));
        setValidationErrors(prev => ({ ...prev, individualId: null }));

        // Extract address from DL response — user_address is an array
        const dlAddresses = result.user_address || [];
        const permanentAddr = dlAddresses.find(a => a.type === 'Permanent') || dlAddresses[0] || {};
        const isMissing = (val) => !val || typeof val !== 'string' || val.trim() === '' || val.trim() === '.' || val.trim().toLowerCase() === 'na';
        const getVal = (val) => isMissing(val) ? '' : val.trim();

        const addressKeys = ['line_1', 'line_2', 'street_name', 'city', 'state', 'zip', 'district_code'];
        const manualAddr = {
          line_1: getVal(permanentAddr.address_line1),
          line_2: '',
          street_name: '',
          city: '',
          state: getVal(permanentAddr.state),
          zip: getVal(permanentAddr.pin),
          district_code: getVal(permanentAddr.district),
        };
        const fullAddress = permanentAddr.complete_address || permanentAddr.address_line1 || '';

        if (setFieldValue) setFieldValue('manualAddress', manualAddr);
        setPrefetchedManualAddress(manualAddr);
        setMissingAddressFields(addressKeys);
        setIsAddressFromDocMissing(true);

        updateSection('businessDetails', {
          fetchedRegisteredAddress: fullAddress,
          fetchedAddressSource: 'Driving Licence',
          prefetchedManualAddress: manualAddr,
          isAddressFromDocMissing: true,
          missingAddressFields: addressKeys,
        });

        if (permanentAddr.pin) {
          setValidatedPincode(permanentAddr.pin);
          updateSection('businessDetails', { validatedPincode: permanentAddr.pin });
        }
      } else if (response.response_code === "106") {
        setValidationErrors(prev => ({
          ...prev,
          individualId: response.response_message || "Invalid ID Number"
        }));
        setBusinessInfo(prev => ({ ...prev, individual: null }));
        setFullBusinessInfo(prev => ({ ...prev, individual: null }));
      } else if (response.response_code === "101") {
        setValidationErrors(prev => ({
          ...prev,
          individualId: response.response_message || "No Record Found"
        }));
      }
    } catch (error) {
      setValidationErrors(prev => ({
        ...prev,
        individualId: error.message || "Error validating ID"
      }));
      setBusinessInfo(prev => ({ ...prev, individual: null }));
      setFullBusinessInfo(prev => ({ ...prev, individual: null }));
    } finally {
      setIsLoading(false);
      setValidatingField(null);
    }
  };

  const initialValues = {
    buyerSellerType: existingData.buyerSellerType || '',
    businessType: existingData.businessType || '',
    natureOfBusiness: existingData.natureOfBusiness || '',
    typeOfIndividual: existingData.typeOfIndividual || '',
    hasGST: existingData.hasGST !== undefined ? existingData.hasGST : true,
    gstNumber: existingData.gstNumber || '',
    hasPAN: existingData.hasPAN !== undefined ? existingData.hasPAN : true,
    panNumber: existingData.panNumber || '',
    hasIndividualDL: existingData.hasIndividualDL !== undefined ? existingData.hasIndividualDL : true,
    individualId: existingData.individualId || '',
    date_of_birth: existingData.date_of_birth || '',
    documentExistsErrors: {},
    isProprietor: existingData.isProprietor || false,
  };

  // const handleSubmit = (values) => {
  //   // Process selected authorized signatory to extract firstName and lastName
  //   let firstName = '';
  //   let lastName = '';

  //   if (selectedAuthorizedSignatory && selectedAuthorizedSignatory.name) {
  //     const fullName = selectedAuthorizedSignatory.name.trim();
  //     const nameParts = fullName.split(' ');

  //     if (nameParts.length > 0) {
  //       firstName = nameParts[0]; // First word
  //       lastName = nameParts.slice(1).join(' '); // Rest of the words
  //     }
  //   }

  //   // Build payload including only enabled document paths
  //   const completeData = {
  //     ...values,
  //     buyerSellerType: values.buyerSellerType,
  //     businessType: values.businessType,
  //     natureOfBusiness: values.natureOfBusiness,
  //     // Only include gst/pan/license when respective toggles are enabled
  //     ...(values.hasGST ? { gst: values.gstNumber, gstValidated: businessInfo.gst } : { gst: '', gstValidated: null }),
  //     ...(values.hasPAN ? { pan: values.panNumber, panValidated: businessInfo.pan } : { pan: '', panValidated: null }),
  //     ...(values.hasIndividualDL ? { license: values.individualId, licenseValidated: businessInfo.individual } : { license: '', licenseValidated: null }),
  //     validatedInfo: {
  //       gst: values.hasGST ? businessInfo.gst : null,
  //       pan: values.hasPAN ? businessInfo.pan : null,
  //       individual: values.hasIndividualDL ? businessInfo.individual : null,
  //     },
  //     fullValidatedInfo: {
  //       gst: values.hasGST ? fullBusinessInfo.gst : null,
  //       pan: values.hasPAN ? fullBusinessInfo.pan : null,
  //       individual: values.hasIndividualDL ? fullBusinessInfo.individual : null,
  //     },
  //     validatedPincode: validatedPincode,
  //     authorizedSignatories: authorizedSignatories,
  //     companyName: companyName,
  //     selectedAuthorizedSignatory: selectedAuthorizedSignatory,
  //     isProprietor: values.isProprietor,
  //     // Add firstName and lastName for save API when authorized signatory is selected
  //     ...(selectedAuthorizedSignatory && {
  //       firstName: firstName,
  //       lastName: lastName
  //     })
  //   };

  //   updateSection('businessDetails', completeData);

  //   // Mark step as complete if required fields are filled
  //   if (values.natureOfBusiness && values.typeOfIndividual) {
  //     if (values.hasGST && businessInfo.gst) {
  //       markStepComplete(1);
  //     } else if (values.hasPAN && businessInfo.pan) {
  //       markStepComplete(1);
  //     } else if (values.hasIndividualDL && businessInfo.individual && values.date_of_birth) {
  //       markStepComplete(1);
  //     } else {
  //       markStepIncomplete(1);
  //     }
  //   } else {
  //     markStepIncomplete(1);
  //   }

  //   onComplete({
  //     businessDetails: {
  //       ...values,
  //       // Ensure disabled paths don't send stale values
  //       ...(values.hasGST ? {} : { gstNumber: '' }),
  //       ...(values.hasPAN ? {} : { panNumber: '' }),
  //       ...(values.hasIndividualDL ? {} : { individualId: '', date_of_birth: '' }),
  //       buyerSellerTypeEnumName: getBuyerSellerEnumName(Number(values?.buyerSellerType)),
  //       businessTypeEnumName: getBusinessTypeEnumName(Number(values?.businessType)),
  //       natureOfBusinessEnumName: getNatureOfBusinessEnumName(Number(values?.natureOfBusiness)),
  //       typeOfIndividualEnumName: getTypeOfIndividualEnumName(Number(values?.typeOfIndividual)),
  //       validatedInfo: {
  //         gst: values.hasGST ? businessInfo.gst : null,
  //         pan: values.hasPAN ? businessInfo.pan : null,
  //         individual: values.hasIndividualDL ? businessInfo.individual : null,
  //       },
  //       fullValidatedInfo: {
  //         gst: values.hasGST ? fullBusinessInfo.gst : null,
  //         pan: values.hasPAN ? fullBusinessInfo.pan : null,
  //         individual: values.hasIndividualDL ? fullBusinessInfo.individual : null,
  //       },
  //       validatedPincode: validatedPincode,
  //       authorizedSignatories: authorizedSignatories,
  //       companyName: companyName,
  //       selectedAuthorizedSignatory: selectedAuthorizedSignatory,
  //       isProprietor: values.isProprietor,
  //       // Add firstName and lastName for save API
  //       ...(selectedAuthorizedSignatory && {
  //         firstName: firstName,
  //         lastName: lastName
  //       })
  //     }
  //   });
  // };
  const handleSubmit = (values) => {
    // 1. Prepare raw validation data - Merging will be handled in the OperationDetails step
    let finalPanResult = businessInfo.pan ? { ...businessInfo.pan } : null;
    let finalFullPanResponse = fullBusinessInfo.pan ? { ...fullBusinessInfo.pan } : null;
    let finalGstResult = businessInfo.gst ? { ...businessInfo.gst } : null;
    let finalFullGstResponse = fullBusinessInfo.gst ? { ...fullBusinessInfo.gst } : null;

    // 2. Extract Enum names for persistence and API
    const buyerSellerTypeEnumName = getBuyerSellerEnumName(Number(values?.buyerSellerType));
    const businessTypeEnumName = getBusinessTypeEnumName(Number(values?.businessType));
    const natureOfBusinessEnumName = getNatureOfBusinessEnumName(Number(values?.natureOfBusiness));
    const typeOfIndividualEnumName = getTypeOfIndividualEnumName(Number(values?.typeOfIndividual));

    // 3. Extract firstName and lastName from authorized signatory
    let firstName = '';
    let lastName = '';

    if (selectedAuthorizedSignatory && selectedAuthorizedSignatory.name) {
      const fullName = selectedAuthorizedSignatory.name.trim();
      const nameParts = fullName.split(' ');

      if (nameParts.length > 0) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
      }
    }

    // 4. Build payload for global state/context
    const completeData = {
      ...values,
      buyerSellerType: values.buyerSellerType,
      businessType: values.businessType,
      natureOfBusiness: values.natureOfBusiness,
      
      // Enum mapping for API
      buyerSellerTypeEnumName,
      businessTypeEnumName,
      natureOfBusinessEnumName,
      typeOfIndividualEnumName,

      // Persistence flags for the next step
      isAddressFromDocMissing: isAddressFromDocMissing,
      missingAddressFields: missingAddressFields,
      prefetchedManualAddress: prefetchedManualAddress,

      // Document inclusion
      ...(values.hasGST ? { gst: values.gstNumber, gstValidated: finalGstResult } : { gst: '', gstValidated: null }),
      ...(values.hasPAN ? { pan: values.panNumber, panValidated: finalPanResult } : { pan: '', panValidated: null }),
      ...(values.hasIndividualDL ? { license: values.individualId, licenseValidated: businessInfo.individual } : { license: '', licenseValidated: null }),

      validatedInfo: {
        gst: values.hasGST ? finalGstResult : null,
        pan: values.hasPAN ? finalPanResult : null,
        individual: values.hasIndividualDL ? businessInfo.individual : null,
      },
      fullValidatedInfo: {
        gst: values.hasGST ? finalFullGstResponse : null,
        pan: values.hasPAN ? finalFullPanResponse : null,
        individual: values.hasIndividualDL ? fullBusinessInfo.individual : null,
      },
      validatedPincode: validatedPincode,
      authorizedSignatories: authorizedSignatories,
      companyName: companyName,
      selectedAuthorizedSignatory: selectedAuthorizedSignatory,
      isProprietor: values.isProprietor,

      ...(selectedAuthorizedSignatory && {
        firstName: firstName,
        lastName: lastName
      })
    };

    updateSection('businessDetails', completeData);

    // 5. Mark step completion
    if (values.natureOfBusiness && values.typeOfIndividual) {
      if (values.hasGST && businessInfo.gst) {
        markStepComplete(1);
      } else if (values.hasPAN && finalPanResult) {
        markStepComplete(1);
      } else if (values.hasIndividualDL && businessInfo.individual && values.date_of_birth) {
        markStepComplete(1);
      } else {
        markStepIncomplete(1);
      }
    } else {
      markStepIncomplete(1);
    }

    // 6. Final callback for OnboardingWizard
    onComplete({
      businessDetails: {
        ...completeData,
        // Ensure disabled paths don't send stale values
        ...(values.hasGST ? {} : { gstNumber: '' }),
        ...(values.hasPAN ? {} : { panNumber: '' }),
        ...(values.hasIndividualDL ? {} : { individualId: '', date_of_birth: '' }),
      }
    });

    // Ensure Next button status is synced on successful submission
    if (setIsNextButtonDisable) {
      setIsNextButtonDisable(false);
    }
  };
  return (
    <div className="step-form">
      <h2>{title}</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        context={{
          missingFields: missingAddressFields,
          isAddressFromDocMissing: isAddressFromDocMissing
        }}
        onSubmit={handleSubmit}
        innerRef={ref}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, touched, errors, setFieldValue, submitForm, validateField, validateForm }) => {
          useImperativeHandle(ref, () => ({
            submitForm: async () => {
              // Validate the entire form first
              const validationErrors = await validateForm();

              // Check if there are any validation errors
              if (Object.keys(validationErrors).length > 0) {
                console.log('Form validation failed:', validationErrors);
                return false; // Prevent submission
              }

              // Check if there are any document existence errors
              if (Object.keys(documentExistsErrors).some(key => documentExistsErrors[key])) {
                console.log('Document existence errors found:', documentExistsErrors);
                return false; // Prevent submission
              }

              // Enforce document validation (must click Validate)
              const selectedEnumNameLocal = getTypeOfIndividualEnumName(Number(values.typeOfIndividual));
              const isDriverOrTransportLocal = selectedEnumNameLocal === 'DriverAndVehicleOwner' || selectedEnumNameLocal === 'TransportCompany';

              // Hard block: if all three are No, do not proceed
              if (values.hasGST === false && values.hasPAN === false && values.hasIndividualDL === false) {
                return false;
              }

              // If GST path chosen
              if (values.hasGST === true) {
                if (!businessInfo.gst) {
                  setValidationErrors(prev => ({ ...prev, gstNumber: 'Please validate your GST number' }));
                  return false;
                }
              } else if (values.hasPAN === true) { // If PAN path chosen (when GST is false)
                if (!businessInfo.pan) {
                  setValidationErrors(prev => ({ ...prev, panNumber: 'Please validate your PAN number' }));
                  return false;
                }
              } else if (values.hasIndividualDL === true) { // If DL path chosen (when GST and PAN are false)
                if (!businessInfo.individual) {
                  setValidationErrors(prev => ({ ...prev, individualId: 'Please validate your Driving License' }));
                  return false;
                }
              }

              // If all validations pass, submit the form
              return submitForm();
            },
            setSelectedAuthorizedSignatory: (signatory) => {
              setSelectedAuthorizedSignatory(signatory);
            }
          }));
          const stopForm = values.hasGST === false && values.hasPAN === false && values.hasIndividualDL === false;
          const selectedEnumName = getTypeOfIndividualEnumName(Number(values.typeOfIndividual));
          const isDriverOrTransport = selectedEnumName === 'DriverAndVehicleOwner' || selectedEnumName === 'TransportCompany';
          const isDriverAndVehicleOwner = selectedEnumName === 'DriverAndVehicleOwner';
          const isTransportCompany = selectedEnumName === 'TransportCompany';
          // Reset isProprietor when not DriverAndVehicleOwner
          React.useEffect(() => {
            if (selectedEnumName !== 'DriverAndVehicleOwner' && values.isProprietor) {
              setFieldValue('isProprietor', false);
            }
          }, [selectedEnumName, setFieldValue, values.isProprietor]);

          // When DriverAndVehicleOwner is selected, enforce only DL path is active
          React.useEffect(() => {
            if (selectedEnumName === 'DriverAndVehicleOwner') {
              if (values.hasGST) setFieldValue('hasGST', false);
              if (values.hasPAN) setFieldValue('hasPAN', false);
              if (!values.hasIndividualDL) setFieldValue('hasIndividualDL', true);
              // Clear GST/PAN values and validated state to avoid hidden-state leakage
              setFieldValue('gstNumber', '');
              setFieldValue('panNumber', '');
              setDocumentExistsErrors(prev => ({ ...prev, gstNumber: null, panNumber: null }));
              setValidationErrors(prev => ({ ...prev, gstNumber: null, panNumber: null }));
              if (businessInfo.gst || businessInfo.pan) {
                setBusinessInfo(prev => ({ ...prev, gst: null, pan: null }));
                setFullBusinessInfo(prev => ({ ...prev, gst: null, pan: null }));
              }
            }
          }, [selectedEnumName]);


          // Default Nature of Business to 9 (Transport) when TransportCompany is selected
          React.useEffect(() => {
            if (selectedEnumName === 'TransportCompany') {
              if (values.natureOfBusiness !== 9) {
                setFieldValue('natureOfBusiness', 9);
              }
            } else if (selectedEnumName === "DriverAndVehicleOwner") {
              // setFieldValue('natureOfBusiness', null);
            }
          }, [selectedEnumName, values.natureOfBusiness, setFieldValue]);

          // Update form values with document existence errors
          React.useEffect(() => {
            setFieldValue('documentExistsErrors', documentExistsErrors);
            // Trigger validation to check the updated documentExistsErrors
            if (Object.keys(documentExistsErrors).length > 0) {
              // Force validation by validating the relevant fields
              if (documentExistsErrors.gstNumber && values.hasGST) {
                validateField('gstNumber');
              }
              if (documentExistsErrors.panNumber && values.hasPAN) {
                validateField('panNumber');
              }
              if (documentExistsErrors.individualId && values.hasIndividualDL) {
                validateField('individualId');
              }
            }
          }, [documentExistsErrors, setFieldValue, validateField, values]);

          // Robust rules mapping for disallowed Nature of Business by Type of Individual
          const natureOfBusinessRules = {
            BusinessOrganisation: {
              disallowEnumNames: ['Transport'],
            },
            // You can extend with other roles later if needed
          };

          const baseNatureOptions = getNatureOfBusinessEnumOptions();
          const disallowList = natureOfBusinessRules[selectedEnumName]?.disallowEnumNames || [];
          const disabledValueSet = new Set(
            disallowList
              .map((enumName) => getNatureOfBusinessValueByEnumName(enumName))
              .filter((v) => v !== null)
          );

          // Build options with disabled flags to prevent selection
          console.log("BaseNatureOptions", baseNatureOptions)
          const natureOptions = baseNatureOptions.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: disabledValueSet.has(opt.value),
          }));

          // Update isNextButtonDisable based on current form state and validation
          React.useEffect(() => {
            if (!setIsNextButtonDisable) return;

            const selectedEnumName = getTypeOfIndividualEnumName(Number(values.typeOfIndividual));
            const isDriverOrTransport = selectedEnumName === 'DriverAndVehicleOwner' || selectedEnumName === 'TransportCompany';

            // 1. Check basic mandatory fields
            let isStepValid = !!values.typeOfIndividual;
            
            if (!isDriverOrTransport) {
              isStepValid = isStepValid && values.buyerSellerType && values.natureOfBusiness;
            } else if (selectedEnumName === 'TransportCompany') {
              isStepValid = isStepValid && values.natureOfBusiness;
            }

            // 2. Check document validation based on selected path
            let isDocValidated = false;
            if (values.hasGST) {
              isDocValidated = !!businessInfo.gst;
            } else if (values.hasPAN) {
              isDocValidated = !!businessInfo.pan;
            } else if (values.hasIndividualDL) {
              isDocValidated = !!businessInfo.individual && !!values.date_of_birth && !!values.individualId;
            } else {
              // If all No, form is stuck anyway per existing logic
              isDocValidated = false;
            }

            // 3. Set Next Button Status
            const isDisabled = !isStepValid || !isDocValidated || isLoading;
            setIsNextButtonDisable(isDisabled);

            console.log('Syncing Next Button State:', { isStepValid, isDocValidated, isDisabled });
          }, [values, businessInfo, isLoading, setIsNextButtonDisable]);

          // If current selection becomes disabled due to rules, clear it
          React.useEffect(() => {
            if (disabledValueSet.has(values.natureOfBusiness)) {
              setFieldValue('natureOfBusiness', '');
            }
          }, [selectedEnumName, setFieldValue]);

          return (
            <Form>
              {/* PillSelector for 'I am a...' (already done) */}
              <div className="form-field">
                <label htmlFor="typeOfIndividual">I am a<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                <PillSelector
                  options={getTypeOfIndividualOptions()}
                  value={values.typeOfIndividual}
                  onChange={val => setFieldValue('typeOfIndividual', val)}
                  multiSelect={false}
                />
                {(touched.typeOfIndividual && errors.typeOfIndividual) && (
                  <div className="error-message">{errors.typeOfIndividual}</div>
                )}
              </div>

              {/* {selectedEnumName === 'DriverAndVehicleOwner' && (
                <div className="form-field">
                  <label>Are you a Proprietor?<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1rem 0' }}>
                    <label className="ncswitch">
                      <Field
                        type="checkbox"
                        name="isProprietor"
                        checked={values.isProprietor}
                        onChange={e => setFieldValue('isProprietor', e.target.checked)}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84' }}>{values.isProprietor ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              )} */}

              {/* Buyer/Seller shown when not Driver or Transport */}
              {!isDriverOrTransport && (
                <div className="form-field">
                  <label htmlFor="buyerSellerType">Biofuels, Briquettes, Pellets, Feedstock, Biomass & much more! Are you here to Buy or Sell?<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                  <PillSelector
                    options={getBuyerSellerTypeOptions()}
                    value={values.buyerSellerType}
                    onChange={val => setFieldValue('buyerSellerType', val)}
                    multiSelect={false}
                  />
                  {(touched.buyerSellerType && errors.buyerSellerType) && (
                    <div className="error-message">{errors.buyerSellerType}</div>
                  )}
                </div>
              )}

              {/* Always show business type except for Driver & Vehicle Owner */}
              {!isDriverAndVehicleOwner && (
                <div className="form-field">
                  <label htmlFor="businessType">What describes you the best?<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                  <PillSelector
                    options={getBusinessTypeOptions()}
                    value={values.businessType}
                    onChange={val => setFieldValue('businessType', val)}
                    multiSelect={false}
                  />
                  {(touched.businessType && errors.businessType) && (
                    <div className="error-message">{errors.businessType}</div>
                  )}
                </div>
              )}

              {/* PillSelector for 'What are you going to Buy/Sell?' (hide for Driver & Transport) */}
              {!isDriverOrTransport && (
                <div className="form-field">
                  <label htmlFor="natureOfBusiness">What are you going to Buy/Sell?<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                  <PillSelector
                    options={natureOptions}
                    value={values.natureOfBusiness}
                    onChange={val => setFieldValue('natureOfBusiness', val)}
                    multiSelect={false}
                  />
                  {(touched.natureOfBusiness && errors.natureOfBusiness) && (
                    <div className="error-message">{errors.natureOfBusiness}</div>
                  )}
                </div>
              )}

              {/* GST Question and Field in one row (hidden for DriverAndVehicleOwner) */}
              {selectedEnumName !== 'DriverAndVehicleOwner' && (
                <div className="form-field">
                  <label>Do you have a GST number?</label>
                  <div style={{ display: 'flex', alignItems: isMobile ? 'start' : 'center', gap: '1rem', margin: '1rem 0', flexDirection: isMobile ? 'column' : 'row', width: '100%', }}>
                    <label className="ncswitch">
                      <Field
                        type="checkbox"
                        name="hasGST"
                        checked={values.hasGST}
                        onChange={e => {
                          setFieldValue('hasGST', e.target.checked);
                           setDocumentExistsErrors(prev => ({ ...prev, gstNumber: null }));
                            setValidationErrors(prev => ({ ...prev, gstNumber: null }));
                            // Clear validated GST info when path is off
                            setBusinessInfo(prev => ({ ...prev, gst: null, pan:null }));
                            setFullBusinessInfo(prev => ({ ...prev, gst: null, pan:null }));
                            // Reset GST form value
                            setFieldValue('gstNumber', '');
                            setFieldValue('date_of_birth', '');
                            setFieldValue('individualId', '');
                            setFieldValue('panNumber', '');
                            setFieldValue('hasPAN', false);
                          // Clear GST document existence error when toggling
                          // if (!e.target.checked) {
                          //   setDocumentExistsErrors(prev => ({ ...prev, gstNumber: null }));
                          //   setValidationErrors(prev => ({ ...prev, gstNumber: null }));
                          //   // Clear validated GST info when path is off
                          //   setBusinessInfo(prev => ({ ...prev, gst: null }));
                          //   setFullBusinessInfo(prev => ({ ...prev, gst: null }));
                          //   // Reset GST form value
                          //   setFieldValue('gstNumber', '');
                          //   setFieldValue('date_of_birth', '');
                          //   setFieldValue('individualId', '');
                          //   setFieldValue('panNumber', '');
                          //   setFieldValue('hasPAN', false);
                          // }
                        }}
                        disabled={isLoading}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84' }}>{values.hasGST ? 'Yes' : 'No'}</span>
                    {values.hasGST && (
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Field
                          type="text"
                          name="gstNumber"
                          id="gstNumber"
                          className={`form-input ${touched.gstNumber && (errors.gstNumber || validationErrors.gstNumber) ? 'error' : ''}`}
                          placeholder="Enter your GST number"
                          disabled={isLoading}
                          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, flex: 1, borderRight: 'none', height: '50px' }}
                          onChange={(e) => {
                            const value = e.target.value;
                            const upperCaseValue = convertLettersToUppercase(value);
                            setFieldValue('gstNumber', upperCaseValue);
                            // Clear GST document existence error when user changes the input
                            if (documentExistsErrors.gstNumber) {
                              setDocumentExistsErrors(prev => ({ ...prev, gstNumber: null }));
                            }
                            // Clear GST validation error when user changes the input
                            if (validationErrors.gstNumber) {
                              setValidationErrors(prev => ({ ...prev, gstNumber: null }));
                            }
                            // Clear validated GST info when user changes the input
                            if (businessInfo.gst) {
                              setBusinessInfo(prev => ({ ...prev, gst: null }));
                              setFullBusinessInfo(prev => ({ ...prev, gst: null }));
                            }
                          }}
                        />
                        <button
                          type="button"
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            background: isLoading || !/^[A-Za-z0-9]{15}$/.test(values.gstNumber) || errors.gstNumber || documentExistsErrors.gstNumber ? '#6c757d' : '#1b62ab',
                            color: '#fff',
                            border: `1px solid ${isLoading || !/^[A-Za-z0-9]{15}$/.test(values.gstNumber) || errors.gstNumber || documentExistsErrors.gstNumber ? '#6c757d' : '#1b62ab'}`,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            padding: '0 16px',
                            cursor: (isLoading || !/^[A-Za-z0-9]{15}$/.test(values.gstNumber) || errors.gstNumber || documentExistsErrors.gstNumber) ? 'not-allowed' : 'pointer',
                            opacity: (isLoading || !/^[A-Za-z0-9]{15}$/.test(values.gstNumber) || errors.gstNumber || documentExistsErrors.gstNumber) ? 0.6 : 1,
                            height: '50px',
                            minWidth: '100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0px 0.375rem 0.375rem 0px',
                            gap: '8px'
                          }}
                          disabled={isLoading || !/^[A-Za-z0-9]{15}$/.test(values.gstNumber) || errors.gstNumber || documentExistsErrors.gstNumber}
                          onClick={() => handleGSTValidation(values.gstNumber, setFieldValue)}
                        >
                          {isLoading && validatingField === 'gstNumber' ? (
                            <>
                              <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                              Validating...
                            </>
                          ) : (
                            'Validate'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <ErrorMessage name="hasGST" component="div" className="error-message" />
                  {values.hasGST && (
                    <>
                      <ErrorMessage name="gstNumber" component="div" className="error-message" />
                      {validationErrors.gstNumber && <div className="error-message">{validationErrors.gstNumber}</div>}
                      {documentExistsErrors.gstNumber && <div className="error-message">{documentExistsErrors.gstNumber}</div>}
                    </>
                  )}
                  {businessInfo.gst?.legal_name && values.hasGST && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '0.5rem',
                      color: '#28a745'
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="10" fill="#7ED957" />
                          <path d="M6 10.5L9 13.5L14 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>{businessInfo.gst.legal_name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* PAN Question and Field in one row */}
              {values.hasGST === false && selectedEnumName !== 'DriverAndVehicleOwner' && (
                <div className="form-field">
                  <label>Do you have a Business PAN number?</label>
                  <div style={{ display: 'flex', alignItems: isMobile ? 'start' : 'center', gap: '1rem', margin: '1rem 0', flexDirection: isMobile ? 'column' : 'row', width: '100%', }}>
                    <label className="ncswitch">
                      <Field
                        type="checkbox"
                        name="hasPAN"
                        checked={values.hasPAN}
                        onChange={e => {
                          setFieldValue('hasPAN', e.target.checked);
                          setDocumentExistsErrors(prev => ({ ...prev, panNumber: null }));
                            setValidationErrors(prev => ({ ...prev, panNumber: null }));
                            setBusinessInfo(prev => ({ ...prev, pan: null, gst: null }));
                            setFullBusinessInfo(prev => ({ ...prev, pan: null, gst: null }));
                            // Reset PAN form value
                            setFieldValue('panNumber', '');
                            setFieldValue('gstNumber', '');
                            setFieldValue('date_of_birth', '');
                            setFieldValue('individualId', '');
                            setFieldValue('hasGST', false);
                            setMissingAddressFields([]);
setIsAddressFromDocMissing(false);
                          // Clear PAN document existence error when toggling
                          // if (!e.target.checked) {
                          //   setDocumentExistsErrors(prev => ({ ...prev, panNumber: null }));
                          //   setValidationErrors(prev => ({ ...prev, panNumber: null }));
                          //   setBusinessInfo(prev => ({ ...prev, pan: null }));
                          //   setFullBusinessInfo(prev => ({ ...prev, pan: null }));
                          //   // Reset PAN form value
                          //   setFieldValue('panNumber', '');
                          //   setFieldValue('gstNumber', '');
                          //   setFieldValue('date_of_birth', '');
                          //   setFieldValue('individualId', '');
                          //   setFieldValue('hasGST', false);
                          // }
                        }}
                        disabled={isLoading}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84' }}>{values.hasPAN ? 'Yes' : 'No'}</span>
                    {values.hasPAN && (
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Field
                          type="text"
                          name="panNumber"
                          id="panNumber"
                          className={`form-input ${touched.panNumber && (errors.panNumber || validationErrors.panNumber) ? 'error' : ''}`}
                          placeholder="Enter your Business PAN number"
                          disabled={isLoading}
                          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, flex: 1, borderRight: 'none', height: '50px' }}
                          onChange={(e) => {
                            const value = e.target.value;
                            const upperCaseValue = convertLettersToUppercase(value);
                            setFieldValue('panNumber', upperCaseValue);
                            // Clear PAN document existence error when user changes the input
                            if (documentExistsErrors.panNumber) {
                              setDocumentExistsErrors(prev => ({ ...prev, panNumber: null }));
                            }
                            // Clear PAN validation error when user changes the input
                            if (validationErrors.panNumber) {
                              setValidationErrors(prev => ({ ...prev, panNumber: null }));
                            }
                            // Clear validated PAN info when user changes the input
                            if (businessInfo.pan) {
                              setBusinessInfo(prev => ({ ...prev, pan: null }));
                              setFullBusinessInfo(prev => ({ ...prev, pan: null }));
                            }
                          }}
                        />
                        <button
                          type="button"
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            background: isLoading || !/^[A-Za-z0-9]{10}$/.test(values.panNumber) || errors.panNumber || documentExistsErrors.panNumber ? '#6c757d' : '#1b62ab',
                            color: '#fff',
                            border: `1px solid ${isLoading || !/^[A-Za-z0-9]{10}$/.test(values.panNumber) || errors.panNumber || documentExistsErrors.panNumber ? '#6c757d' : '#1b62ab'}`,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            padding: '0 16px',
                            cursor: (isLoading || !/^[A-Za-z0-9]{10}$/.test(values.panNumber) || errors.panNumber || documentExistsErrors.panNumber) ? 'not-allowed' : 'pointer',
                            opacity: (isLoading || !/^[A-Za-z0-9]{10}$/.test(values.panNumber) || errors.panNumber || documentExistsErrors.panNumber) ? 0.6 : 1,
                            height: '50px',
                            minWidth: '100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0px 0.375rem 0.375rem 0px',
                            gap: '8px'
                          }}
                          disabled={isLoading || !/^[A-Za-z0-9]{10}$/.test(values.panNumber) || errors.panNumber || documentExistsErrors.panNumber}
                          onClick={() => handleBusinessPANValidation(values.panNumber, setFieldValue)}
                        >
                          {isLoading && validatingField === 'panNumber' ? (
                            <>
                              <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                              Validating...
                            </>
                          ) : (
                            'Validate'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <ErrorMessage name="hasPAN" component="div" className="error-message" />
                  <ErrorMessage name="panNumber" component="div" className="error-message" />
                  {validationErrors.panNumber && <div className="error-message">{validationErrors.panNumber}</div>}
                  {documentExistsErrors.panNumber && <div className="error-message">{documentExistsErrors.panNumber}</div>}
                  {businessInfo.pan?.user_full_name && values.hasPAN && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '0.5rem',
                      color: '#28a745'
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="10" fill="#7ED957" />
                          <path d="M6 10.5L9 13.5L14 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>{businessInfo.pan.user_full_name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* {isAddressFromDocMissing && (values.hasGST || values.hasPAN) && (
                <div className="form-field">
                  <label htmlFor="registeredAddress">
                    Registered Address<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span>
                  </label>
                  <Field
                    as="textarea"
                    name="registeredAddress"
                    id="registeredAddress"
                    className={`form-input ${validationErrors.registeredAddress ? 'error' : ''}`}
                    placeholder="Enter full registered address"
                    rows={3}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateSection('businessDetails', { fetchedRegisteredAddress: value });
                    }}
                  />
                </div>
              )} */}

              {/* Driving License (shown only for DriverAndVehicleOwner) */}
              {selectedEnumName === 'DriverAndVehicleOwner' && (
                <div className="form-field">
                  <label>Do you have a Driving License?</label>
                  <div style={{ display: 'flex', alignItems: isMobile ? 'start' : 'center', gap: '1rem', margin: '1rem 0', flexDirection: isMobile ? 'column' : 'row', width: '100%', }}>
                    <label className="ncswitch">
                      <Field
                        type="checkbox"
                        name="hasIndividualDL"
                        checked={values.hasIndividualDL}
                        onChange={e => {
                          setFieldValue('hasIndividualDL', e.target.checked);
                          // Clear Individual ID document existence error when toggling
                          if (!e.target.checked) {
                            setDocumentExistsErrors(prev => ({ ...prev, individualId: null }));
                            setValidationErrors(prev => ({ ...prev, individualId: null, date_of_birth: null }));
                            setBusinessInfo(prev => ({ ...prev, individual: null }));
                            setFullBusinessInfo(prev => ({ ...prev, individual: null }));
                            // Reset DL form values
                            setFieldValue('individualId', '');
                            setFieldValue('date_of_birth', '');
                            setFieldValue('panNumber', '');
                            setFieldValue('gstNumber', '');
                          }
                        }}
                        disabled={isLoading}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84' }}>{values.hasIndividualDL ? 'Yes' : 'No'}</span>
                    {values.hasIndividualDL && (
                      <div style={{ flex: 1, width: '100%' }}>
                        <label htmlFor="date_of_birth" style={{ marginBottom: 4, display: 'block' }}>Date of Birth</label>
                        <Field
                          type="date"
                          name="date_of_birth"
                          id="date_of_birth"
                          className={`form-input ${touched.date_of_birth && errors.date_of_birth ? 'error' : ''}`}
                          placeholder="dd-mm-yyyy"
                          max={new Date().toISOString().split('T')[0]}
                          disabled={isLoading}
                          style={{ height: '50px' }}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('date_of_birth', value);
                            // Clear validated Individual ID info when user changes the date of birth
                            if (businessInfo.individual) {
                              setBusinessInfo(prev => ({ ...prev, individual: null }));
                              setFullBusinessInfo(prev => ({ ...prev, individual: null }));
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {values.hasIndividualDL && (
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}>
                      <Field
                        type="text"
                        name="individualId"
                        id="individualId"
                        className={`form-input ${touched.individualId && (errors.individualId || validationErrors.individualId) ? 'error' : ''}`}
                        placeholder="Enter your Driving License number"
                        disabled={isLoading}
                        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, flex: 1, borderRight: 'none', height: '50px' }}
                        onChange={(e) => {
                          const value = e.target.value;
                          const upperCaseValue = convertLettersToUppercase(value);
                          setFieldValue('individualId', upperCaseValue);
                          // Clear Individual ID document existence error when user changes the input
                          if (documentExistsErrors.individualId) {
                            setDocumentExistsErrors(prev => ({ ...prev, individualId: null }));
                          }
                          // Clear Individual ID validation error when user changes the input
                          if (validationErrors.individualId) {
                            setValidationErrors(prev => ({ ...prev, individualId: null }));
                          }
                          // Clear validated Individual ID info when user changes the input
                          if (businessInfo.individual) {
                            setBusinessInfo(prev => ({ ...prev, individual: null }));
                            setFullBusinessInfo(prev => ({ ...prev, individual: null }));
                          }
                        }}
                      />
                      <button
                        type="button"
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          background: isLoading || !values.individualId || !values.date_of_birth || errors.individualId || errors.date_of_birth || documentExistsErrors.individualId ? '#6c757d' : '#1b62ab',
                          color: '#fff',
                          border: `1px solid ${isLoading || !values.individualId || !values.date_of_birth || errors.individualId || errors.date_of_birth || documentExistsErrors.individualId ? '#6c757d' : '#1b62ab'}`,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          padding: '0 16px',
                          cursor: (isLoading || !values.individualId || !values.date_of_birth || errors.individualId || errors.date_of_birth || documentExistsErrors.individualId) ? 'not-allowed' : 'pointer',
                          opacity: (isLoading || !values.individualId || !values.date_of_birth || errors.individualId || errors.date_of_birth || documentExistsErrors.individualId) ? 0.6 : 1,
                          height: '50px',
                          minWidth: '100px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '0px 0.375rem 0.375rem 0px',
                          gap: '8px'
                        }}
                        disabled={isLoading || !values.individualId || !values.date_of_birth || errors.individualId || errors.date_of_birth || documentExistsErrors.individualId}
                        onClick={() => handleIndividualPANValidation(values.individualId, values.date_of_birth, setFieldValue)}
                      >
                        {isLoading && validatingField === 'individualId' ? (
                          <>
                            <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                            Validating...
                          </>
                        ) : (
                          'Validate'
                        )}
                      </button>
                    </div>
                  )}
                  <ErrorMessage name="hasIndividualDL" component="div" className="error-message" />
                  <ErrorMessage name="date_of_birth" component="div" className="error-message" />
                  <ErrorMessage name="individualId" component="div" className="error-message" />
                  {validationErrors.individualId && <div className="error-message">{validationErrors.individualId}</div>}
                  {documentExistsErrors.individualId && <div className="error-message">{documentExistsErrors.individualId}</div>}
                  {businessInfo.individual?.user_full_name && values.hasIndividualDL && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '0.5rem',
                      color: '#28a745'
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="10" fill="#7ED957" />
                          <path d="M6 10.5L9 13.5L14 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>{businessInfo.individual.user_full_name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Stop form if all are No */}
              {stopForm && (
                <div className="mt-4" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc3545' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="10" fill="#dc3545" />
                      <path d="M6 6L14 14M14 6L6 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>Sorry, you cannot proceed without GST, PAN, or Individual PAN/Driving License. The application will now close.</span>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
});

export default BusinessDetails;
